
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useForm } from "@/context/FormContext";
import { FormFieldType } from "@/types/form";
import FieldWrapper from "../FieldWrapper";
import { Teacher, useTeacherSearch } from "./useTeacherSearch";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";

interface TeacherSearchFieldProps {
  element: FormFieldType;
}

export const TeacherSearchField: React.FC<TeacherSearchFieldProps> = ({ element }) => {
  const { formData, setField } = useForm();
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  
  // Initialize selected teacher from form data if available
  useEffect(() => {
    if (element.name && formData[element.name]) {
      const teacherId = formData[element.name];
      // Check if the value is a string before using it
      if (typeof teacherId === 'string') {
        setSelectedTeacher({ id: teacherId, label: teacherId });
      }
    }
  }, [formData, element.name]);
  
  const { 
    searchQuery, 
    setSearchQuery, 
    teachers, 
    isLoading 
  } = useTeacherSearch(true, () => {});

  // Handle teacher selection
  const handleSelectTeacher = (option: any) => {
    if (!option) {
      handleClearSelection();
      return;
    }
    
    const teacher = { id: option.value, label: option.label };
    setSelectedTeacher(teacher);
    
    // Update the form data with the selected teacher
    if (element.name) {
      setField(element.name, teacher.label); // Store the full teacher label
    }

    // If this has a target field for teacher ID, update that field too
    if (element.targetField) {
      setField(element.targetField, teacher.id); // Store just the teacher ID in the target field
    }
    
    toast({
      title: t('form.fields.selectedTeacherTitle'),
      description: `${teacher.label}`,
    });
  };

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedTeacher(null);
    setSearchQuery("");
    if (element.name) {
      setField(element.name, "");
    }
    
    // Clear the target field as well if specified
    if (element.targetField) {
      setField(element.targetField, "");
    }
  };

  // Convert teachers array to options format for react-select
  const options = teachers.map(teacher => ({
    value: teacher.id,
    label: teacher.label
  }));

  // Handle input change for search
  const handleInputChange = (inputValue: string) => {
    setSearchQuery(inputValue);
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '40px',
      borderRadius: '0.375rem',
      borderColor: '#e2e8f0',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#cbd5e1',
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: '0.375rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#e2e8f0' : undefined,
      color: state.isSelected ? 'white' : 'inherit',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#3b82f6',
      },
    }),
  };

  return (
    <FieldWrapper element={element}>
      <Select
        options={options}
        value={selectedTeacher ? { value: selectedTeacher.id, label: selectedTeacher.label } : null}
        onChange={handleSelectTeacher}
        onInputChange={handleInputChange}
        isLoading={isLoading}
        isClearable={true}
        placeholder={t('form.fields.teacherSearchPlaceholder')}
        noOptionsMessage={() => searchQuery.length < 2 ? t('form.fields.searchMin2Chars') : t('form.fields.teacherNoResults')}
        styles={customStyles}
        className="react-select-container"
        classNamePrefix="react-select"
        menuPlacement={isMobile ? "auto" : "bottom"}
        menuPosition="fixed"
      />
    </FieldWrapper>
  );
};

export default TeacherSearchField;
